<script setup lang="ts">
import { ref, watch, computed } from "vue";

interface Props {
  block: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  update: [block: any];
}>();

const localBlock = ref(JSON.parse(JSON.stringify(props.block)));

// Inicializar dados se não existirem
if (!localBlock.value.data) {
  localBlock.value.data = {
    title: "Tabela",
    dataKey: "parametros",
    columns: [],
    data: [],
  };
}

if (!localBlock.value.style) {
  localBlock.value.style = {
    border: "1px solid #dee2e6",
  };
}

// Computed properties para os controles
const tableHeaderColor = computed({
  get: () => localBlock.value.style["--header-bg"] || "#f8f9fa",
  set: (value) => {
    localBlock.value.style["--header-bg"] = value;
    onChange();
  },
});

const stripedRows = computed({
  get: () => localBlock.value.style["--striped"] === "true",
  set: (value) => {
    localBlock.value.style["--striped"] = value.toString();
    onChange();
  },
});

const roundedBorder = computed({
  get: () => localBlock.value.style.borderRadius === "8px",
  set: (value) => {
    localBlock.value.style.borderRadius = value ? "8px" : "0";
    onChange();
  },
});

watch(
  () => props.block,
  (newBlock) => {
    localBlock.value = JSON.parse(JSON.stringify(newBlock));
  },
  { deep: true }
);

function onChange() {
  emit("update", localBlock.value);
}

function onDataKeyChange() {
  // Carregar colunas padrão baseado na chave selecionada
  const columnsMap: any = {
    parametros: [
      { key: "parameter", label: "Parâmetro", editable: true },
      { key: "value", label: "Valor Medido", editable: true },
      { key: "limit", label: "Limite", editable: true },
      { key: "unit", label: "Unidade", editable: true },
      { key: "status", label: "Status", editable: false },
    ],
    resultados: [
      { key: "item", label: "Item", editable: true },
      { key: "result", label: "Resultado", editable: true },
      { key: "reference", label: "Referência", editable: true },
    ],
    limites: [
      { key: "substance", label: "Substância", editable: true },
      { key: "limit", label: "Limite", editable: true },
      { key: "unit", label: "Unidade", editable: true },
    ],
  };

  if (localBlock.value.data.dataKey !== "custom") {
    localBlock.value.data.columns =
      columnsMap[localBlock.value.data.dataKey] || [];
  }

  onChange();
}

function addColumn() {
  if (!localBlock.value.data.columns) {
    localBlock.value.data.columns = [];
  }
  localBlock.value.data.columns.push({
    key: `col${localBlock.value.data.columns.length + 1}`,
    label: `Coluna ${localBlock.value.data.columns.length + 1}`,
    editable: true,
  });
  onChange();
}

function removeColumn(index: number) {
  localBlock.value.data.columns.splice(index, 1);
  onChange();
}

function onHeaderColorChange() {
  onChange();
}

function onStripedChange() {
  onChange();
}

function onRoundedChange() {
  onChange();
}
</script>

<template>
  <div class="table-edit">
    <h4>Editar Tabela</h4>

    <div class="form-group">
      <label>Título:</label>
      <input
        v-model="localBlock.data.title"
        @input="onChange"
        type="text"
        placeholder="Título da tabela"
      />
    </div>

    <div class="form-group">
      <label>Chave dos Dados:</label>
      <select v-model="localBlock.data.dataKey" @change="onDataKeyChange">
        <option value="parametros">Parâmetros Avaliados</option>
        <option value="resultados">Resultados</option>
        <option value="limites">Limites Toleráveis</option>
        <option value="custom">Personalizado</option>
      </select>
    </div>

    <div class="columns-section">
      <div class="section-header">
        <label>Colunas:</label>
        <button @click="addColumn" type="button" class="btn-add">+</button>
      </div>

      <div
        v-for="(column, index) in localBlock.data.columns"
        :key="index"
        class="column-item"
      >
        <input
          v-model="column.label"
          @input="onChange"
          placeholder="Nome da coluna"
          class="column-label"
        />
        <input
          v-model="column.key"
          @input="onChange"
          placeholder="Chave"
          class="column-key"
        />
        <label class="checkbox-label">
          <input type="checkbox" v-model="column.editable" @change="onChange" />
          Editável
        </label>
        <button @click="removeColumn(index)" type="button" class="btn-remove">
          ×
        </button>
      </div>
    </div>

    <div class="form-group">
      <label>Estilo da Tabela:</label>
      <select v-model="localBlock.style.border" @change="onChange">
        <option value="1px solid #dee2e6">Borda Padrão</option>
        <option value="2px solid #333">Borda Forte</option>
        <option value="none">Sem Borda</option>
      </select>
    </div>

    <div class="form-group">
      <label>Cor do Cabeçalho:</label>
      <input
        type="color"
        v-model="tableHeaderColor"
        @change="onHeaderColorChange"
      />
    </div>

    <div class="form-group">
      <label>Linhas Zebradas:</label>
      <input type="checkbox" v-model="stripedRows" @change="onStripedChange" />
    </div>

    <div class="form-group">
      <label>Borda Arredondada:</label>
      <input
        type="checkbox"
        v-model="roundedBorder"
        @change="onRoundedChange"
      />
    </div>
  </div>
</template>

<style scoped>
.table-edit {
  padding: 10px 0;
}

h4 {
  margin: 0 0 15px 0;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
  font-size: 13px;
}

input[type="text"],
select,
input[type="color"] {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

input[type="checkbox"] {
  margin-right: 8px;
}

.columns-section {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.btn-add {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #218838;
  }
}

.column-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.column-label {
  flex: 2;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.column-key {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 12px;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 11px;
  white-space: nowrap;
}

.btn-remove {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: #c82333;
  }
}
</style>
